/** 来源：market ToastProvider/useToast + CRM ToastStack；统一 aria-live、自动消失与手动关闭。 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { alertToneClasses, type Tone } from './tones'

export interface ToastItem {
  id: number
  text: string
  tone: Tone
}

export interface ToastNotice {
  id: number
  message: string
  tone: 'success' | 'error'
}

export type Toast = (text: string, tone?: Tone, duration?: number) => void

const ToastContext = createContext<Toast | null>(null)

function ToastNotice({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  return (
    <div
      className={`pointer-events-auto flex items-center gap-2 rounded-[var(--radius-md)] border-l-4 px-3.5 py-2.5 text-sm shadow-[var(--shadow-md)] ${alertToneClasses[item.tone]}`}
      role={item.tone === 'danger' ? 'alert' : 'status'}
      aria-live="polite"
    >
      <span className="min-w-0 flex-1">{item.text}</span>
      <button
        type="button"
        className="rounded-[var(--radius-sm)] px-1 text-[var(--color-muted)] hover:bg-[var(--color-soft)] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)]"
        aria-label="关闭通知"
        onClick={() => onDismiss(item.id)}
      >
        ×
      </button>
    </div>
  )
}

type ToastStackProps =
  | { items: ToastItem[]; notices?: never; onDismiss: (id: number) => void; duration?: number }
  | { items?: never; notices: ToastNotice[]; onDismiss: (id: number) => void; duration?: number }

export function ToastStack({ items, notices, onDismiss, duration }: ToastStackProps) {
  const controlledItems: ToastItem[] = items ?? notices.map((notice) => ({
    id: notice.id,
    text: notice.message,
    tone: notice.tone === 'error' ? 'danger' : 'success'
  }))

  useEffect(() => {
    if (!notices?.length) return
    const timers = notices.map((notice) => setTimeout(() => onDismiss(notice.id), duration ?? 3600))
    return () => timers.forEach(clearTimeout)
  }, [duration, notices, onDismiss])

  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-72 max-w-[calc(100vw-2rem)] flex-col gap-2" aria-label="通知">
      {controlledItems.map((item) => <ToastNotice key={item.id} item={item} onDismiss={onDismiss} />)}
    </div>
  )
}

export function ToastProvider({ children, duration = 3500 }: { children: ReactNode; duration?: number }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const push = useCallback<Toast>((text, tone = 'success', toastDuration) => {
    const id = nextId.current++
    setItems((current) => [...current, { id, text, tone }])
    const wait = toastDuration ?? duration
    if (wait > 0) timers.current.set(id, setTimeout(() => dismiss(id), wait))
  }, [dismiss, duration])

  useEffect(() => () => {
    for (const timer of timers.current.values()) clearTimeout(timer)
    timers.current.clear()
  }, [])

  return (
    <ToastContext.Provider value={push}>
      {children}
      <ToastStack items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): Toast {
  const toast = useContext(ToastContext)
  if (!toast) throw new Error('useToast 必须在 ToastProvider 内使用')
  return toast
}
